import { useState, useRef, type DragEvent, type ChangeEvent } from 'react';
import toast from 'react-hot-toast';
import { api } from '@/lib/api';
import { GlassCard } from '@/components/ui/GlassCard';
import { PageHeader } from '@/components/ui/PageHeader';
import { useMonthStore } from '@/lib/monthStore';
import { cn } from '@/lib/cn';

interface UploadResult {
  monthId: string;
  daysProcessed: number;
  totalRevenue: number;
  avgDqs: number;
}

export default function Upload() {
  const [file, setFile] = useState<File | null>(null);
  const [month, setMonth] = useState<number>(new Date().getMonth() + 1);
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [isDragging, setIsDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<UploadResult | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const setSelectedMonthId = useMonthStore((s) => s.setSelectedMonthId);

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped) handleFile(dropped);
  };

  const onFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (selected) handleFile(selected);
  };

  const handleFile = (f: File) => {
    if (!f.name.match(/\.(xlsx|xls)$/i)) {
      toast.error('Solo se aceptan archivos .xlsx o .xls');
      return;
    }
    setFile(f);
    setResult(null);
    const match = f.name.match(/(\w+)\s*(\d{4})/i);
    if (match) {
      const monthNames = [
        'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
        'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
      ];
      const monthIdx = monthNames.findIndex((m) =>
        match[1].toLowerCase().includes(m),
      );
      if (monthIdx >= 0) setMonth(monthIdx + 1);
      const yr = parseInt(match[2], 10);
      if (yr > 2000 && yr < 2100) setYear(yr);
    }
  };

  const onUpload = async () => {
    if (!file) return;
    setLoading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('month', String(month));
    formData.append('year', String(year));

    try {
      const res = await api.upload<UploadResult>('/parser/upload', formData);
      setResult(res);
      setSelectedMonthId(res.monthId);
      toast.success(`Cargado: ${res.daysProcessed} días, DQS ${res.avgDqs.toFixed(1)}/100`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Error al cargar';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageHeader
        tag="// MÓDULO :: 06 / INGEST"
        title="Ingesta de Datos"
        subtitle="Sube el archivo Excel mensual de operación"
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <GlassCard
          variant="kinetic"
          padding="md"
          rounded="none"
          className="lg:col-span-2"
        >
          <div className="mb-4 pb-3 border-b-2 border-outline">
            <p className="font-mono text-[10px] uppercase tracking-widest text-secondary">
              // FILE_UPLOAD
            </p>
            <h3 className="font-condensed text-2xl uppercase tracking-tight text-text-bright mt-1">
              Archivo Excel
            </h3>
          </div>

          <div
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={onDrop}
            onClick={() => inputRef.current?.click()}
            className={cn(
              'p-16 text-center cursor-pointer border-2 border-dashed transition-all duration-150',
              isDragging
                ? 'bg-surface border-primary shadow-[4px_4px_0_0_#ff8a65]'
                : 'bg-surface border-outline hover:border-primary',
            )}
          >
            <input
              ref={inputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={onFileChange}
              className="hidden"
            />
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center bg-primary border-2 border-black text-black shadow-[3px_3px_0_0_#000000]">
              <span
                className="material-symbols-outlined text-5xl"
                style={{ fontVariationSettings: "'FILL' 1" }}
              >
                cloud_upload
              </span>
            </div>
            {file ? (
              <>
                <p className="font-bold text-text-bright text-lg font-mono uppercase tracking-widest">
                  {file.name}
                </p>
                <p className="font-mono text-[10px] uppercase tracking-widest text-text-muted mt-2 num">
                  {(file.size / 1024).toFixed(1)} KB
                </p>
              </>
            ) : (
              <>
                <p className="font-bold text-text-bright text-lg font-condensed uppercase tracking-tight">
                  Arrastra tu archivo aquí
                </p>
                <p className="font-mono text-[10px] uppercase tracking-widest text-text-muted mt-2">
                  o haz click para seleccionar (.xlsx, .xls, máx 10MB)
                </p>
              </>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4 mt-6">
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-widest text-secondary mb-2">
                // MES
              </label>
              <select
                value={month}
                onChange={(e) => setMonth(parseInt(e.target.value, 10))}
                className="kinetic-input cursor-pointer"
              >
                {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                  <option key={m} value={m}>
                    {new Date(2000, m - 1, 1).toLocaleDateString('es-MX', {
                      month: 'long',
                    })}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-widest text-secondary mb-2">
                // AÑO
              </label>
              <input
                type="number"
                value={year}
                onChange={(e) => setYear(parseInt(e.target.value, 10))}
                min={2020}
                max={2100}
                className="kinetic-input num"
              />
            </div>
          </div>

          <button
            onClick={onUpload}
            disabled={!file || loading}
            className="kinetic-button w-full mt-6"
          >
            {loading && (
              <span className="material-symbols-outlined animate-spin">progress_activity</span>
            )}
            <span className="material-symbols-outlined">upload</span>
            Procesar archivo
          </button>
        </GlassCard>

        <GlassCard variant="kinetic" padding="md" rounded="none">
          <div className="mb-4 pb-3 border-b-2 border-outline">
            <p className="font-mono text-[10px] uppercase tracking-widest text-secondary">
              // STATUS
            </p>
            <h3 className="font-condensed text-2xl uppercase tracking-tight text-text-bright mt-1">
              Estado del proceso
            </h3>
          </div>

          {result ? (
            <div className="space-y-3">
              <div className="bg-surface border-2 border-tertiary p-4">
                <p className="font-mono text-[10px] uppercase tracking-widest text-tertiary font-bold mb-1">
                  ✓ CARGA EXITOSA
                </p>
                <p className="text-text-bright text-sm font-mono">
                  Mes procesado y seleccionado.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-surface border-2 border-outline p-3">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-text-muted">
                    DÍAS
                  </p>
                  <p className="font-condensed text-3xl text-text-bright num">
                    {result.daysProcessed}
                  </p>
                </div>
                <div className="bg-surface border-2 border-outline p-3">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-text-muted">
                    DQS
                  </p>
                  <p className="font-condensed text-3xl text-tertiary num">
                    {result.avgDqs.toFixed(1)}
                  </p>
                </div>
                <div className="bg-surface border-2 border-outline p-3 col-span-2">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-text-muted">
                    INGRESOS TOTALES
                  </p>
                  <p className="font-condensed text-2xl text-primary num">
                    $
                    {result.totalRevenue.toLocaleString('es-MX', {
                      minimumFractionDigits: 2,
                    })}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-text-muted text-center py-8">
              <div className="w-16 h-16 mx-auto mb-3 flex items-center justify-center bg-surface border-2 border-outline text-text-muted">
                <span
                  className="material-symbols-outlined text-4xl"
                  style={{ fontVariationSettings: "'FILL' 1" }}
                >
                  hourglass_empty
                </span>
              </div>
              <p className="font-mono text-[10px] uppercase tracking-widest text-text-muted">
                // Sube un archivo para ver el resultado
              </p>
            </div>
          )}

          <div className="mt-6 pt-6 border-t-2 border-outline">
            <h4 className="font-mono text-[10px] uppercase tracking-widest text-secondary mb-3">
              // ESQUEMA
            </h4>
            <ul className="space-y-2 text-xs">
              {[
                'Una hoja por día del mes',
                '12 SKUs de empanada por hoja',
                'Pagos (Efectivo, Tarjeta, RAPPI, UBER)',
                'Caja (Fondo Fijo, Gastos, Total en Caja)',
              ].map((line) => (
                <li
                  key={line}
                  className="flex items-center gap-2 font-mono uppercase tracking-widest text-[10px] text-text-bright"
                >
                  <span className="w-5 h-5 flex items-center justify-center bg-tertiary text-black border-2 border-black">
                    <span className="material-symbols-outlined text-xs">check</span>
                  </span>
                  {line}
                </li>
              ))}
            </ul>
          </div>
        </GlassCard>
      </div>
    </>
  );
}
